/**
 * Downloads' fixture — 1:20244.
 *
 * Placeholder for now: there is no endpoint behind this page, so every figure
 * below matches the reference implementation rather than a response.
 *
 * NEEDS MANUAL CORRECTION when the section gets an API. Installer sizes and
 * hrefs, the server name and the broker-time note are the parts that will come
 * from it; which platforms exist and what they are called is editorial and may
 * well stay here.
 *
 * Windows, macOS, iOS, and Android carry verified destinations. Rows without a
 * destination are left without one rather than guessed at: `InfoRow` renders a
 * row with no destination as a plain div, so nothing claims to be clickable.
 */

import { currentLanguage, t } from '../../i18n/i18n';

/** Store listings — the MetaTrader 5 rows and the Alpha Capital app both point here. */
export const APP_STORE_URL = 'https://apps.apple.com/in/app/alpha-capital-group/id6745693120';
export const GOOGLE_PLAY_URL = 'https://play.google.com/store/apps/details?id=uk.acg.android&pli=1';

/** MetaQuotes' own MetaTrader 5 desktop installers, as linked from metatrader5.com/en/download. */
export const MT5_WINDOWS_URL = 'https://download.terminal.free/cdn/web/metaquotes.ltd/mt5/mt5setup.exe';
export const MT5_MACOS_URL = 'https://download.terminal.free/cdn/web/metaquotes.ltd/mt5/MetaTrader5.pkg.zip';

/**
 * cTrader, as the broker's own welcome email links it. Those links are Brevo
 * click-tracking redirects; these are the destinations they resolve to, so the
 * page doesn't depend on a campaign link staying alive.
 */
export const CTRADER_WINDOWS_URL = 'https://getctrader.com/acgmarkets/ctrader-acgmarkets-setup.exe';
export const CTRADER_MACOS_URL = 'https://getctradermac.com/acgmarkets/ctrader-acgmarkets-setup.dmg';
export const CTRADER_WEB_URL = 'https://app.acg-markets.com/';

/** DX Trade and TradeLocker, resolved from the same welcome email's tracking links. */
export const DXTRADE_WEB_URL = 'https://dxtrade.alphacapitalgroup.uk/';
export const TRADELOCKER_WEB_URL = 'https://demo.tradelocker.com/';
export const TRADELOCKER_APP_STORE_URL = 'https://apps.apple.com/uy/app/tradelocker/id6447196449';
export const TRADELOCKER_GOOGLE_PLAY_URL = 'https://play.google.com/store/apps/details?id=com.tradelocker.mobile';

/**
 * The MT5 web terminal, when there is one (TI review N45). Unset, the "Web
 * terminal" row isn't drawn: mt5.alphacapital.com/terminal, the address it
 * used to point at, fails its SSL handshake and never opens. Set
 * REACT_APP_MT5_WEB_TERMINAL_URL to a working address and the row is back.
 */
export const MT5_WEB_TERMINAL_URL = process.env.REACT_APP_MT5_WEB_TERMINAL_URL || null;

/** The banner under the page title. */
export const NOTICE = 'MetaTrader 5 is not available to US residents.';

/** The primary platform, and the one the accounts run on. */
export const PRIMARY = {
  title: 'MetaTrader 5',
  meta: 'Not available to US residents',
  downloads: [
    // Only with a link to open — see MT5_WEB_TERMINAL_URL.
    ...(MT5_WEB_TERMINAL_URL
      ? [
          {
            id: 'web',
            label: 'Web terminal',
            note: 'No install needed, opens in this browser',
            logo: '/images/platform-web-terminal.svg',
            href: MT5_WEB_TERMINAL_URL,
          },
        ]
      : []),
    {
      id: 'windows',
      label: 'Windows',
      note: 'Installer, 62 MB',
      logo: '/images/platform-windows.svg',
      href: MT5_WINDOWS_URL,
    },
    {
      id: 'macos',
      label: 'macOS',
      note: 'Installer, 78 MB',
      logo: '/images/platform-macos.svg',
      href: MT5_MACOS_URL,
    },
    {
      id: 'ios',
      label: 'iOS',
      note: 'App Store',
      logo: '/images/platform-ios.svg',
      href: `https://apps.apple.com/us/app/metatrader-5/id413251709`,
    },
    {
      id: 'android',
      label: 'Android',
      note: 'Google Play',
      logo: '/images/platform-android.svg',
      href: `https://play.google.com/store/apps/details?id=net.metaquotes.metatrader5&hl=en_IN`,
    },
  ],
};

/** Alpha Capital's own mobile app, as opposed to the MetaTrader 5 clients above. */
export const MOBILE_APPS = [
  {
    id: 'app-store',
    label: 'Download on App Store',
    note: 'iPhone and iPad',
    logo: '/images/platform-ios.svg',
    href: APP_STORE_URL,
  },
  {
    id: 'google-play',
    label: 'Download on Google Play',
    note: 'Android',
    logo: '/images/platform-android.svg',
    href: GOOGLE_PLAY_URL,
  },
];

/** The `n`th Sunday of a month (0-based month), as a local date. */
const nthSunday = (year, month, n) => {
  const firstDay = new Date(year, month, 1).getDay();
  return new Date(year, month, 1 + ((7 - firstDay) % 7) + 7 * (n - 1));
};

/**
 * US daylight saving for a year: the second Sunday of March to the first
 * Sunday of November. The MT5 server follows it (GMT+2, GMT+3 while it runs).
 */
export const usDaylightSaving = (year) => ({
  start: nthSunday(year, 2, 2),
  end: nthSunday(year, 10, 1),
});

/** "8 Mar" — in the reader's language ("8 mars", "8. März"). */
const dayMonth = (date) =>
  new Intl.DateTimeFormat(currentLanguage() === 'en' ? 'en-GB' : currentLanguage(), {
    day: 'numeric',
    month: 'short',
  }).format(date);

/**
 * The broker-time note, with this year's daylight-saving dates — or next
 * year's once this year's window has closed. Worked out from the date rather
 * than written in (TI review E9): the dates move every year.
 */
export const brokerTimeNote = (now = new Date()) => {
  let { start, end } = usDaylightSaving(now.getFullYear());
  if (now >= end) ({ start, end } = usDaylightSaving(now.getFullYear() + 1));
  return t('GMT+2 (GMT+3 from {{start}} to {{end}}, while US daylight saving is active)', {
    start: dayMonth(start),
    end: dayMonth(end),
  });
};

/**
 * The two settings a trader has to type into whichever client they installed.
 * A function, so the broker-time dates are worked out when the page renders.
 */
export const connectionFields = (now = new Date()) => [
  { id: 'server', label: 'Server', value: 'ACGMarkets-Main', copy: true },
  {
    id: 'time',
    label: 'Broker time',
    value: brokerTimeNote(now),
    // A clause, not a value — a fifth of the row would break it over four lines.
    wide: true,
  },
];

/** Everything that is not MetaTrader 5. */
export const OTHER_PLATFORMS = [
  {
    id: 'ctrader',
    label: 'cTrader',
    note: 'Web and desktop. Closed to new US registrations',
    logo: '/images/platform-ctrader.png',
    // One row that opens onto its destinations — see `InfoAccordion`.
    options: [
      { id: 'windows', label: 'Windows', note: 'Installer', href: CTRADER_WINDOWS_URL, logo: '/images/platform-windows.svg' },
      { id: 'macos', label: 'macOS', note: 'Installer', href: CTRADER_MACOS_URL, logo: '/images/platform-macos.svg' },
      { id: 'web', label: 'Web app', note: 'Opens in this browser', href: CTRADER_WEB_URL, logo: '/images/platform-web-terminal.svg' },
    ],
  },
  {
    id: 'dxtrade',
    label: 'DX Trade',
    note: 'Web',
    logo: '/images/platform-dxtrade.png',
    href: DXTRADE_WEB_URL,
  },
  {
    id: 'tradelocker',
    label: 'TradeLocker',
    note: 'Web and mobile',
    logo: '/images/platform-tradelocker.png',
    options: [
      { id: 'web', label: 'Web app', note: 'Opens in this browser', href: TRADELOCKER_WEB_URL, logo: '/images/platform-web-terminal.svg' },
      { id: 'ios', label: 'iOS and macOS', note: 'App Store', href: TRADELOCKER_APP_STORE_URL, logo: '/images/platform-ios.svg' },
      { id: 'android', label: 'Android', note: 'Google Play', href: TRADELOCKER_GOOGLE_PLAY_URL, logo: '/images/platform-android.svg' },
    ],
  },
];
